/**
 * AppPermissionsFilters Component
 *
 * Filter bar with dropdown selects and search input for the OAuth apps table.
 * Shows active filter pills with removal and a clear-all action.
 */

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { AcknowledgmentFilterStatus } from './appPermissionsTypes'

interface AppPermissionsFiltersProps {
  riskTier: string | undefined
  appType: string | undefined
  status: string | undefined
  verified: string | undefined
  acknowledgmentStatus: AcknowledgmentFilterStatus | undefined
  search: string
  onRiskTierChange: (v: string | undefined) => void
  onAppTypeChange: (v: string | undefined) => void
  onStatusChange: (v: string | undefined) => void
  onVerifiedChange: (v: string | undefined) => void
  onAcknowledgmentStatusChange: (v: AcknowledgmentFilterStatus | undefined) => void
  onSearchChange: (v: string) => void
  onClearAll: () => void
}

const selectClasses =
  'bg-[color:var(--color-paper,#fff)] border border-[color:var(--color-line,#E6E8EC)] rounded-[4px] px-4 py-2.5 text-[color:var(--color-navy,#212938)] text-sm focus:border-[color:var(--color-secondaryBlue-300,#4F86A8)] focus:ring-1 focus:ring-[color:var(--color-secondaryBlue-300,#4F86A8)]/40 transition-all duration-200 w-full appearance-none cursor-pointer'

interface FilterPill {
  label: string
  onRemove: () => void
}

export function AppPermissionsFilters({
  riskTier,
  appType,
  status,
  verified,
  acknowledgmentStatus,
  search,
  onRiskTierChange,
  onAppTypeChange,
  onStatusChange,
  onVerifiedChange,
  onAcknowledgmentStatusChange,
  onSearchChange,
  onClearAll,
}: AppPermissionsFiltersProps) {
  const activePills: FilterPill[] = []

  if (riskTier) {
    activePills.push({ label: `Risk: ${riskTier}`, onRemove: () => onRiskTierChange(undefined) })
  }
  if (appType) {
    activePills.push({ label: `Type: ${appType}`, onRemove: () => onAppTypeChange(undefined) })
  }
  if (status) {
    activePills.push({ label: `Status: ${status}`, onRemove: () => onStatusChange(undefined) })
  }
  if (verified) {
    activePills.push({ label: `Verified: ${verified}`, onRemove: () => onVerifiedChange(undefined) })
  }
  if (acknowledgmentStatus) {
    activePills.push({ label: `Review: ${acknowledgmentStatus}`, onRemove: () => onAcknowledgmentStatusChange(undefined) })
  }
  if (search) {
    activePills.push({ label: `Search: "${search}"`, onRemove: () => onSearchChange('') })
  }

  const hasFilters = activePills.length > 0

  return (
    <div className="mb-6 space-y-3">
      {/* Filter controls row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <select
          value={riskTier || ''}
          onChange={e => onRiskTierChange(e.target.value || undefined)}
          className={selectClasses}
        >
          <option value="">All Risk Tiers</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>

        <select
          value={appType || ''}
          onChange={e => onAppTypeChange(e.target.value || undefined)}
          className={selectClasses}
        >
          <option value="">All Apps</option>
          <option value="NonMicrosoft">Non-Microsoft</option>
          <option value="InHouse">In-house</option>
          <option value="ThirdParty">Third-party</option>
          <option value="Microsoft">Microsoft</option>
        </select>

        <select
          value={status || ''}
          onChange={e => onStatusChange(e.target.value || undefined)}
          className={selectClasses}
        >
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Stale">Stale</option>
          <option value="Unknown">Unknown</option>
          <option value="Disabled">Disabled</option>
        </select>

        <select
          value={verified || ''}
          onChange={e => onVerifiedChange(e.target.value || undefined)}
          className={selectClasses}
        >
          <option value="">All Publishers</option>
          <option value="Verified">Verified</option>
          <option value="Unverified">Unverified</option>
        </select>

        <select
          value={acknowledgmentStatus || ''}
          onChange={e => onAcknowledgmentStatusChange((e.target.value || undefined) as AcknowledgmentFilterStatus | undefined)}
          className={selectClasses}
        >
          <option value="">All Review Statuses</option>
          <option value="Unreviewed">Unreviewed</option>
          <option value="Approved">Approved</option>
          <option value="Investigating">Investigating</option>
          <option value="Flagged">Flagged</option>
        </select>

        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--color-mute,#6B7280)]" />
          <input
            type="text"
            placeholder="Search apps..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className="bg-[color:var(--color-paper,#fff)] border border-[color:var(--color-line,#E6E8EC)] rounded-[4px] pl-10 pr-4 py-2.5 text-[color:var(--color-navy,#212938)] text-sm focus:border-[color:var(--color-secondaryBlue-300,#4F86A8)] focus:ring-1 focus:ring-[color:var(--color-secondaryBlue-300,#4F86A8)]/40 transition-all duration-200 w-full placeholder:text-[color:var(--color-faint,#9CA3AF)]"
          />
        </div>
      </div>

      {/* Active filter pills */}
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {activePills.map(pill => (
            <span
              key={pill.label}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] rounded-full bg-[#212938] text-white border border-[#212938]"
            >
              {pill.label}
              <button
                onClick={pill.onRemove}
                className="hover:opacity-80 transition-opacity"
                aria-label={`Remove filter: ${pill.label}`}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <button
            onClick={onClearAll}
            className="text-xs italic text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)] transition-colors ml-1"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  )
}
